import type { ComboBoxInputOption } from '@wix/thunderbolt-components';
import type { TextAlignment } from '@wix/editor-elements-definitions';
import type { CompInfo } from '@wix/editor-elements-types/thunderbolt';
import {
  getScaledFont,
  getInputHeight,
  getRequiredIndicationDisplay,
  getHeightInPixels,
} from '@wix/editor-elements-common-utils';
import type {
  ComboBoxInputNavigationBorderVars,
  ComboBoxInputNavigationCSSVars,
} from './ComboBoxInput.types';

export const isOptionWithSelectedText = (
  options: Array<ComboBoxInputOption>,
  optionValue: string,
) => {
  const selectedText = options.find(
    option => option.value === optionValue,
  )?.selectedText;

  return selectedText !== null && selectedText !== undefined;
};

export const noop = () => {};

export const calculateElemWidth = (elem: HTMLElement) =>
  elem.getBoundingClientRect().width;

export const filterOptionsByQuery = (
  filterQuery: string,
  options: Array<ComboBoxInputOption>,
) => {
  const lowerQuery = filterQuery.toLowerCase();

  return options
    .filter(option => option.text.toLowerCase().includes(lowerQuery))
    .sort((prev, next) => {
      const prevLower = prev.text.toLowerCase();
      const nextLower = next.text.toLowerCase();
      const prevStartsWith = prevLower.startsWith(lowerQuery);
      const nextStartsWith = nextLower.startsWith(lowerQuery);

      if (prevStartsWith !== nextStartsWith) {
        return prevStartsWith ? -1 : 1;
      }

      return prevLower.localeCompare(nextLower);
    });
};

export const optionsMapper = (options: Array<ComboBoxInputOption>) => {
  return options.map((option: ComboBoxInputOption) => ({
    label: option.text,
    value: option.value,
  }));
};

export const getComboBoxInputNavigationCSSVars = (
  {
    compProps,
    compSingleLayout,
    styleProperties,
    compLayout,
    isMobileView,
    siteFonts,
    isOneDocMigrated,
  }: Pick<
    CompInfo,
    | 'compProps'
    | 'styleProperties'
    | 'compLayout'
    | 'isMobileView'
    | 'siteFonts'
    | 'isOneDocMigrated'
  > &
    Partial<Pick<CompInfo, 'compSingleLayout'>>,
  alignText: TextAlignment,
  borders: ComboBoxInputNavigationBorderVars,
): ComboBoxInputNavigationCSSVars => {
  const isRightAligned = alignText === 'right';
  const height = isOneDocMigrated
    ? getHeightInPixels(compSingleLayout)
    : compLayout.height;

  return {
    '--direction': isRightAligned ? 'rtl' : 'ltr',
    '--arrowInsetInlineStart': 'auto',
    '--arrowInsetInlineEnd': '0',
    '--align': alignText,
    '--textPaddingInput_start': `20px`,
    '--textPaddingInput_end': `45px`,
    '--fnt': getScaledFont(
      { styleProperties, compLayout, compProps, isMobileView, siteFonts },
      'fnt',
    ),
    '--inputHeight': getInputHeight({
      inputHeightProps: compProps,
      height,
      isMobileView,
    }),
    '--requiredIndicationDisplay':
      getRequiredIndicationDisplay(styleProperties),
    '--borderColor': 'rgba(0, 0, 0, 0.2)',
    ...borders,
  };
};
